import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import { AiOutlineCalendar } from "react-icons/ai";
import { useMutation, gql } from "@apollo/client";

const DELETE_BLOCKED_SLOT = gql`
  mutation deleteBlockedSlots($data: ID!) {
    deleteBlockedSlots(data: $data) {
      status
    }
  }
`;

function DatePickerWithIcon({ selectedDate, setSelectedDate }) {
  const inputRef = useRef(null);

  return (
    <div className="mb-4 relative max-w-xs">
      <label className="block text-xs font-medium mb-1 text-gray-600">
        Select Date
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="border border-gray-300 bg-gray-50 text-gray-900 px-3 py-2 pl-10 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7F58E6] text-sm"
        />
        <AiOutlineCalendar
          className="absolute left-3 top-2.5 text-gray-500 cursor-pointer"
          size={18}
          onClick={() => inputRef.current?.showPicker?.()}
        />
      </div>
      <p className="mt-1 text-[11px] text-gray-500">
        {moment(selectedDate).format("ddd, MMM D")}
      </p>
    </div>
  );
}

const BlockedSlotsList = ({ employeeId, fetchBlockedSlots, selectedDate, setSelectedDate }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const res = await fetchBlockedSlots({
        employeeId,
        date: selectedDate,
      });
      setSlots(res?.blockedSlots || []);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setSlots([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (employeeId && fetchBlockedSlots && selectedDate) {
      fetchSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, selectedDate]);

  const [deleteSlot] = useMutation(DELETE_BLOCKED_SLOT, {
    onCompleted: () => fetchSlots(),
  });

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this slot?"
    );
    if (!confirmDelete) return;
    try {
      await deleteSlot({ variables: { data: id } });
    } catch (err) {
      alert("Failed to delete slot");
    }
  };

  return (
    <div className="space-y-4">
      <DatePickerWithIcon
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />

      {loading ? (
        <p className="text-sm text-gray-500">Loading blocked slots...</p>
      ) : slots.length === 0 ? (
        <p className="text-sm text-gray-500">
          No blocked slots for{" "}
          <span className="text-[#7F58E6]">
            {moment(selectedDate).format("ddd, MMM D")}
          </span>
          .
        </p>
      ) : (
        <div className="space-y-2">
          {slots.map((slot) => (
            <div
              key={slot.id}
              className="border border-gray-200 rounded-xl px-4 py-3 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            >
              <div className="text-sm text-gray-700">
                <p>
                  <span className="text-gray-500">From:</span>{" "}
                  <span className="font-semibold">{slot.startSlot}</span>{" "}
                  <span className="text-gray-500">to</span>{" "}
                  <span className="font-semibold">{slot.endSlot}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Reason:{" "}
                  <span className="text-gray-700">
                    {slot.reason || "N/A"}
                  </span>
                </p>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  className="bg-red-600 hover:bg-red-700 text-gray-900 px-3 py-1.5 rounded-full text-xs font-medium"
                  onClick={() => handleDelete(slot.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlockedSlotsList;
